<script>
import { TeleportBus } from './teleport-bus'

/**
 * Clona (raso) um VNode preservando o contrato do `cloneVNode` interno do Vue 2.
 *
 * Por que isso é necessário: o `<Teleport>` publica os VNODES do seu slot no
 * TeleportBus e o `<TeleportTarget>` os renderiza em outro ponto da árvore. Em
 * Vue 2 um VNode é de uso único — depois de patcheado ele carrega `.elm`. Se o
 * target re-renderiza (ex.: a nav-bar depende de `$route` e re-renderiza na
 * troca de aba) e re-usa a MESMA instância de VNode vinda do bus, o `patchVnode`
 * faz `if (oldVnode === vnode) return` (early-bail) e o conteúdo some — esse era
 * o bug do "Adicionar cliente" sumindo ao navegar entre as abas. Clonar quebra a
 * identidade do VNode a cada render do target, então o patch volta a criar/mover
 * o DOM corretamente, independente do timing da origem.
 */
function cloneVNode(vnode) {
  if (!vnode || typeof vnode !== 'object' || !vnode.constructor) return vnode

  const VNodeClass = vnode.constructor
  const cloned = new VNodeClass(
    vnode.tag,
    vnode.data,
    // Clona o array de filhos para não mutar o original ao clonar um filho.
    vnode.children && vnode.children.slice(),
    vnode.text,
    vnode.elm,
    vnode.context,
    vnode.componentOptions,
    vnode.asyncFactory
  )
  cloned.ns = vnode.ns
  cloned.isStatic = vnode.isStatic
  cloned.key = vnode.key
  cloned.isComment = vnode.isComment
  cloned.fnContext = vnode.fnContext
  cloned.fnOptions = vnode.fnOptions
  cloned.fnScopeId = vnode.fnScopeId
  cloned.asyncMeta = vnode.asyncMeta
  cloned.isCloned = true
  return cloned
}

/**
 * <TeleportTarget name="nome"> — destino do teleport
 * Lê `TeleportBus.targets[name]` (reativo) e renderiza CLONES dos vnodes que a
 * origem (`<Teleport to="nome">`) publicou. Se não houver nada publicado, cai no
 * slot default (fallback).
 */
export default {
  name: 'TeleportTarget',
  props: {
    name: {
      type: String,
      required: true,
    },
    // Tag do wrapper
    tag: {
      type: String,
      default: 'div',
    },
  },
  computed: {
    published() {
      return TeleportBus.targets[this.name]
    },
    hasPublished() {
      return Array.isArray(this.published) && this.published.length > 0
    },
  },
  render(h) {
    const source = this.hasPublished ? this.published : this.$slots.default
    // Renderiza clones: cada render do target produz vnodes "frescos", evitando
    // o early-bail do patch (oldVnode === vnode) que fazia o conteúdo sumir.
    //
    // Chave por DONO: quando a view publicante muda (troca de aba), o `owner` do
    // destino muda -> a key dos vnodes muda -> o patch RECRIA o conteúdo (instância
    // fresca) em vez de reusar a anterior. Sem isso, duas views que publicam o
    // MESMO componente topo (ex: <BasePageHeader>) reusam a instância e mantêm o
    // scoped-slot stale (bug: filtro de leads aparecendo no relacionamento).
    // Dentro do mesmo dono, a key é estável -> reuso normal no re-render.
    const owner = TeleportBus.owners[this.name]
    const content = Array.isArray(source)
      ? source.map((vnode, i) => {
          const cloned = cloneVNode(vnode)
          if (cloned && this.hasPublished) {
            const baseKey = vnode && vnode.key != null ? vnode.key : i
            cloned.key = 'tp-' + owner + '-' + baseKey
          }
          return cloned
        })
      : source
    return h(this.tag, { class: 'teleport-target' }, content)
  },
}
</script>
